import { useResourceAdmission } from "../access/useResourceAdmission";
import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import axios, { isAxiosError } from "axios";
import {
    hydrate,
    MutationCache,
    onlineManager,
    Query,
    QueryClient,
    QueryClientProvider,
    QueryKey,
} from "react-query";
import type { PersistedClient } from "react-query/persistQueryClient-experimental";
import localforage from "localforage";
import { readQueryCacheEpoch } from "./queryCacheEpoch";
import {
    offlineAccessAllowed,
    protectedCacheAuthority,
    resourceAuthorityEpoch,
    resourceAccessState,
    setResourceAccessScope,
    subscribeResourceAccess,
} from "../access/resourceAccess";
import { useCompatMember, useCompatOrganization } from "../hooks/stytch-compat";
import { DOCUMENT_AREA_QUERY_KEY_ROOTS, PrimeQueryKeys } from "../shared/constants";
import { getCookie } from "../util/cookie";
import { PrimeDebugLog } from "../util/debugLog";
import { useGlobalErrorSnackbarStore } from "../store/globalErrorSnackbarStore";
import { previewManager } from "../util/PreviewManager";
import { getPlatform } from "../platform/platformRef";
import { usePlatform } from "../platform/PlatformProvider";
import { e2ePublish } from "../platform/e2e";
import {
    hintStaleEmptyAnswers,
    isPersistedQueryKey,
    persistedAllowanceBytes,
    persistedByteAllowance,
    persistedDependents,
    persistedDependencies,
    PERSISTED_QUERY_STALE_TIME,
    type QueryCacheStore,
} from "../platform/queryPersistence";
import { sessionIdentityFromJwt } from "../platform/tenantIdentity";
import type { LastSessionMemory, SessionIdentity } from "../platform/Platform";
import { registerAnswerFirstQueryClient } from "../native/nativeAnswerFirst";
import { disposeQueryClientResources, setActiveTenantQueryClient } from "./queryClientLifecycle";
import { useEmbedAuth } from "./EmbedAuthContext";
import { useTenantScope } from "./TenantScopeContext";
import { currentTenantContentScopeKey, subscribeTenantContentScopeKey } from "./tenantScopeHeaders";
import { TenantOrganizationIdContext } from "./TenantIdentityContext";
import EmptyState from "../components/documents/common/EmptyState";
import { Quiet } from "../components/documents/design/Quiet";
import { AccessSuspensionCurtain } from "../access/AccessSuspensionCurtain";

export { DOCUMENT_AREA_QUERY_KEY_ROOTS } from "../shared/constants";
// Re-exported so desktop/viewer's own files (outside client/src's node_modules walk-up,
// see desktop/viewer/vite.config.ts's alias comment) can observe the same singleton
// configureTauriQueryNetworkMode below configures, without a second react-query install.
export { onlineManager } from "react-query";

// Cookie name for enabling local cache
const ENABLE_LOCAL_CACHE_COOKIE = "prime_enable_local_cache";

// Bump when changing API return values, the durable-cache schema, or the
// dehydration allowlist shape.
// Org-only 0.2.x snapshots are deliberately not migrated into this API-base-
// scoped schema: the backend that produced an old snapshot is unknowable, so
// assigning it to the currently selected environment could leak data across
// local, staging, and production. 0.3.x snapshots predate the document-area
// allowlist + SAS redaction and are likewise dropped rather than migrated.
// 0.4.x snapshots predate the shop-review persistence exclusion and may carry
// another member's per-user package data; they are dropped, and the restore
// path additionally filters shop-scoped keys (see deserializePersistedClient).
// 0.5.x snapshots carry raw query state (a mid-fetch `isFetching: true` could
// hydrate) and redact SAS values on restore; 0.6.0 writes a normalized state per
// query and zeroes a redacted query's `dataUpdatedAt` at write time.
const CACHE_BUSTER = "0.8.0"; // Bounded browser startup snapshots; preserve invalidation across reloads.

/** First query-key segment; see `DOCUMENT_AREA_QUERY_KEY_ROOTS` for the allowlist policy. */
function queryKeyRoot(queryKey: QueryKey): unknown {
    return Array.isArray(queryKey) ? queryKey[0] : queryKey;
}

/**
 * Determines whether a query is eligible for durable persistence by the default
 * document-area allowlist.
 *
 * Queries that are still loading or have errored are never persisted — rehydrating
 * those would resurrect a stale spinner or error instead of real data. When
 * `restrictToDocumentArea` is true (no local-cache opt-in cookie), the query's first
 * key segment must additionally be one of `DOCUMENT_AREA_QUERY_KEY_ROOTS`. A key a
 * screen registered through `persistQueryKeys` bypasses this allowlist (see
 * `initializePersistence`). Exported for unit testing.
 */
export function shouldDehydrateQuery(query: Query, restrictToDocumentArea: boolean): boolean {
    if (queryKeyRoot(query.queryKey) === "project-access") return false;
    if (query.state.status !== "success" || query.state.data === undefined) {
        return false;
    }
    if (!restrictToDocumentArea) {
        return true;
    }
    const root = queryKeyRoot(query.queryKey);
    return (
        typeof root === "string" &&
        (DOCUMENT_AREA_QUERY_KEY_ROOTS as readonly string[]).includes(root)
    );
}

// Cache configuration constants
const CACHE_TIME = 60 * 60 * 1000; // 1 hour
const STALE_TIME = PERSISTED_QUERY_STALE_TIME; // 5 minutes
const MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days
// Status polling elsewhere in the app (uploads, ingestion) fires every 4-5s
// and touches the query cache, which would re-trigger a persist on every tick
// at a 1s throttle. A wider throttle keeps the snapshot build and the store
// write off the main thread for routine background polling.
const THROTTLE_TIME = 10 * 1000; // 10 seconds
// The first write after a change waits this long so one burst of query
// completions (a screen's fan-out) lands as one write, not one per query.
const WRITE_DELAY = 1000; // 1 second
// A host that publishes its own store (`Platform.queryCache`: the desktop's Rust page
// cache) hydrates before the first tenant render, and that render waits for the snapshot
// read this long. A hit is one local IPC read plus a gunzip and a parse of the snapshot
// (tens of ms); a miss resolves at once. The alternative to waiting is the network: a
// screen that mounted under an empty client fetches everything (about a second), so a
// store that is merely slow -- the native store busy with a sync pass -- is worth a few
// hundred ms. Past the deadline the client activates without the snapshot, and the late
// answer still hydrates whatever the live cache has not settled. (The first merged tour
// measured a 2 s deadline stacked on the session exchange at 3.7 s to Home; the exchange
// no longer gates the render.) The `shell:query-cache:restore` measure reports the real
// read time under e2e. Browser hosts use the much shorter deadline below.
const RESTORE_DEADLINE = 250; // 250 ms
// Give a quick IndexedDB read a chance to supply startup data before mount.
// Slow storage stops gating readiness after this shorter wait budget.
const BROWSER_RESTORE_DEADLINE = 50;
/** What `restore` reports under e2e: how the snapshot read ended. */
type RestoreOutcome = "hit" | "miss" | "deadline" | "rejected";
const CACHE_DATABASE_PREFIX = "primepoint-cache-";
// Generic fallback shown by the global MutationCache error handler below. A
// mutation-local `onError` suppresses it automatically; callers that surface an
// error outside that callback opt out via `meta: { suppressGlobalError: true }`.
const GENERIC_MUTATION_ERROR_MESSAGE = "Something went wrong. Please try again.";
const CACHE_STORE_NAME = "prime-cache";
const CACHE_DATABASE_REGISTRY_KEY = "primepoint-cache-databases";
// Scopes written to a host `Platform.queryCache` store, remembered for logout: a
// host store has no enumeration, so the scopes to remove come from here.
const QUERY_CACHE_SCOPE_REGISTRY_KEY = "primepoint-query-cache-scopes";

/** One tenant's IndexedDB database, as localforage exposes it. */
type TenantStore = LocalForage;

interface PersistenceSession {
    stop: () => Promise<void>;
    /** Writes a snapshot now when one is waiting on the throttle; resolves once the
     *  store has taken it. */
    flush: () => Promise<void>;
}

/** What `initializePersistence` hands the provider: when the snapshot is in the
 *  client (or given up on), and how to stop writing. */
interface PersistenceHandle {
    ready: Promise<void>;
    stop(): void;
}

const INERT_PERSISTENCE: PersistenceHandle = { ready: Promise.resolve(), stop: () => undefined };

// Keep the actual per-org instances available for logout. indexedDB.databases()
// is not implemented in every supported browser, and the default localforage
// singleton cannot clear stores created through createInstance().
const tenantStoresByDatabase = new Map<string, Set<TenantStore>>();
const activePersistenceSessions = new Set<PersistenceSession>();
const legacyCacheRetirements = new Map<string, Promise<void>>();
let persistenceGeneration = 0;
let cacheClearInProgress = false;
let cacheClearPromise: Promise<void> | null = null;

/**
 * Resolve the backend identity used by this page. configureNativeApiOrigin()
 * sets axios.defaults.baseURL before React mounts under Capacitor; plain web
 * stays same-origin. Preserve the normalized pathname because Axios keeps a
 * configured base path: https://host/env-a and https://host/env-b can route to
 * different backends even though URL.origin is identical. Invalid/non-HTTP(S)
 * or credential/query/fragment-bearing values fail closed by disabling durable
 * persistence instead of assigning them an ambiguous namespace.
 */
export function resolveCacheApiBase(): string | null {
    if (typeof window === "undefined") {
        return null;
    }

    const configuredBaseUrl = axios.defaults.baseURL;
    if (
        typeof configuredBaseUrl === "string" &&
        configuredBaseUrl.length > 0 &&
        configuredBaseUrl !== configuredBaseUrl.trim()
    ) {
        // Axios retains surrounding whitespace in its string-based join, while
        // URL parsing trims it. Do not let the cache identity collapse those
        // differently routed bases.
        return null;
    }
    const rawBase =
        typeof configuredBaseUrl === "string" && configuredBaseUrl.length > 0
            ? configuredBaseUrl
            : window.location.origin;

    try {
        // URL drops delimiter-only `?` / `#` and treats `\` as `/`, while
        // Axios keeps them in its string-based baseURL join. Reject those
        // spellings before parsing so distinct routed URLs cannot collide with
        // the normal pathname-only base.
        if (rawBase.includes("?") || rawBase.includes("#") || rawBase.includes("\\")) {
            return null;
        }

        const resolved = new URL(rawBase, window.location.origin);
        if (
            (resolved.protocol !== "http:" && resolved.protocol !== "https:") ||
            resolved.username ||
            resolved.password ||
            resolved.search ||
            resolved.hash
        ) {
            return null;
        }
        // Match Axios combineURLs(): it removes at most two trailing slashes.
        // Three slashes therefore still route with a doubled slash before the
        // request path and must remain a distinct backend/cache identity.
        const pathname = resolved.pathname.replace(/\/?\/$/, "");
        return `${resolved.origin}${pathname}`;
    } catch {
        return null;
    }
}

function tenantCacheScope(organizationId: string, apiBase: string, memberId: string): string {
    // Member is a cache dimension (CLAUDE_SHARED §3): shop packages and flags
    // are per-USER, so both the live QueryClient and the durable store must
    // rotate on a same-org member change — an org-only scope served the prior
    // member's private data from memory until the owner-gated refetch failed.
    return `${encodeURIComponent(organizationId)}--${encodeURIComponent(apiBase)}--${encodeURIComponent(memberId)}`;
}

function readStringRegistry(registryKey: string): Set<string> {
    if (typeof localStorage === "undefined") {
        return new Set();
    }

    try {
        const value: unknown = JSON.parse(localStorage.getItem(registryKey) ?? "[]");
        if (!Array.isArray(value)) {
            return new Set();
        }
        return new Set(value.filter((name): name is string => typeof name === "string"));
    } catch {
        return new Set();
    }
}

function writeStringRegistry(registryKey: string, values: Set<string>): void {
    if (typeof localStorage === "undefined") {
        return;
    }

    try {
        if (values.size === 0) {
            localStorage.removeItem(registryKey);
        } else {
            localStorage.setItem(registryKey, JSON.stringify(Array.from(values)));
        }
    } catch {
        // Durable persistence can still operate when localStorage is disabled;
        // the in-memory instance registry remains the logout fallback.
    }
}

function readPersistedTenantDatabaseNames(): Set<string> {
    const names = readStringRegistry(CACHE_DATABASE_REGISTRY_KEY);
    names.forEach((name) => {
        if (!name.startsWith(CACHE_DATABASE_PREFIX)) names.delete(name);
    });
    return names;
}

function registerTenantStore(databaseName: string, tenantStore: TenantStore): void {
    const stores = tenantStoresByDatabase.get(databaseName) ?? new Set<TenantStore>();
    stores.add(tenantStore);
    tenantStoresByDatabase.set(databaseName, stores);

    const databaseNames = readPersistedTenantDatabaseNames();
    databaseNames.add(databaseName);
    writeStringRegistry(CACHE_DATABASE_REGISTRY_KEY, databaseNames);
}

/**
 * Remove the pre-origin cache for this org without ever reading or hydrating
 * it. Opening the exact legacy database is required on Safari versions that
 * cannot enumerate IndexedDB databases and for snapshots created before the
 * database registry existed. The old payload has no trustworthy origin, so it
 * is unsafe to migrate into whichever backend happens to be selected now.
 */
function retireLegacyTenantCache(orgSlug: string): void {
    retireLegacyDatabase(`${CACHE_DATABASE_PREFIX}${orgSlug}`);
}

/**
 * Retire the pre-MEMBER-scoped database (`org--apiBase`, 0.4/0.5 era) for the
 * current org/backend. The member-qualified database name means the app never
 * opens that store again, so its buster/restore filters can never run — yet
 * it may still hold a previous member's private shop-package payloads on a
 * shared browser. Clear it explicitly during this upgrade.
 */
function retirePreMemberTenantCache(organizationId: string, apiBase: string): void {
    const preMemberScope = `${encodeURIComponent(organizationId)}--${encodeURIComponent(apiBase)}`;
    retireLegacyDatabase(`${CACHE_DATABASE_PREFIX}${preMemberScope}`);
}

function retireLegacyDatabase(databaseName: string): void {
    if (legacyCacheRetirements.has(databaseName)) {
        return;
    }

    try {
        const legacyStore = localforage.createInstance({
            name: databaseName,
            storeName: CACHE_STORE_NAME,
        });
        registerTenantStore(databaseName, legacyStore);

        const retirement = Promise.resolve()
            .then(() => legacyStore.clear())
            .catch((error) => {
                legacyCacheRetirements.delete(databaseName);
                console.warn(`Failed to retire legacy persisted cache ${databaseName}:`, error);
            });
        legacyCacheRetirements.set(databaseName, retirement);
    } catch (error) {
        console.warn(`Failed to open legacy persisted cache ${databaseName}:`, error);
    }
}

/**
 * The query retry policy, exported for tests.
 *
 * 4xx responses are deterministic — retrying can't succeed and just delays the error
 * UI (the 404 page used to sit blank for ~7s of 1s/2s/4s backoff). 408/429 are
 * transient by definition and keep the react-query default of 3 attempts, as do
 * network errors (no response) and 5xx.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
    if (failureCount >= 3) return false;
    if (isAxiosError(error)) {
        const status = error.response?.status;
        const isTransient4xx = status === 408 || status === 429;
        if (status && status >= 400 && status < 500 && !isTransient4xx) {
            return false;
        }
    }
    return true;
}

/**
 * react-query v3 predates v4's per-query `networkMode: "always"` — there is no knob
 * that stops a failed query's retry from pausing when `navigator.onLine` reads false.
 * The v3 equivalent is the `onlineManager` singleton itself: replace its browser
 * online/offline listener with one that reports "online" once and never listens
 * again, so `onlineManager.isOnline()` stays permanently true and a retry is never
 * paused on a connectivity guess (see `feedback_no_connectivity_gating.md`: fire the
 * request, let the response decide — a stuck-false `navigator.onLine` inside a fresh
 * Tauri WebView must not hang every retry until an 'online' event that may never
 * come). The offline cache in `config/axios.ts` already answers a genuine outage for
 * GETs; this only concerns the retry loop reached when no cached fallback exists.
 *
 * This override alone means `onlineManager` never again reports an "online" TRANSITION
 * once a `QueryClient` has mounted (`onlineManager.onSubscribe` only re-runs `setup`
 * when no cleanup is installed, and this override's cleanup is installed at boot,
 * before any subscriber exists) — so `refetchOnReconnect` never fires on a real
 * recovery either. `native/nativeQueryReconnectTauri.ts`'s `notifyNativeQueryReconnect`
 * is the real signal: it re-arms that transition from the SAME transport-failure/
 * success observation `config/axios.ts` already computes, calling
 * `onlineManager.setOnline(true)` (never `false`) only on the first successful request
 * after a transport failure.
 *
 * `onlineManager` is a react-query-package-level singleton — one shared instance for
 * every module that imports this same bundled copy. Call this only from desktop boot
 * (`main.tsx`), never from render or from a codepath the plain web bundle or the iOS
 * Capacitor shell (their own separate bundle, where a real offline state should
 * still pause retries) can reach. No-op outside Tauri, exported for tests.
 */
export function configureTauriQueryNetworkMode(): void {
    if (getPlatform().bootstrap.queryNetworkMode !== "offlineFirst") return;
    onlineManager.setEventListener((setOnline) => {
        setOnline(true);
        return () => undefined;
    });
}

/** Appends a short reference to the server's request id (returned as
 *  `X-Request-ID` on every API response) so support can find the failure in
 *  the logs. Errors without a response keep the bare message. */
function withRequestReference(message: string, error: unknown): string {
    const requestId = isAxiosError(error) ? error.response?.headers?.["x-request-id"] : undefined;
    return typeof requestId === "string" && requestId
        ? `${message} (ref: ${requestId.slice(0, 8)})`
        : message;
}

/**
 * Create a new QueryClient with default options. Exported for tests.
 *
 * The MutationCache-level onError is a last-resort net: it fires for EVERY
 * mutation failure across the app, so a mutation that never wires up its own
 * error handling no longer fails completely silently. A mutation-local
 * `onError` suppresses this fallback automatically. Callers that surface a
 * specific error outside `onError` opt out with `meta: { suppressGlobalError:
 * true }` — e.g. `useCreateConversationAndNavigate` sets it when the caller
 * renders its own "Couldn't send your message" snackbar from the result.
 */
export function createQueryClient(): QueryClient {
    return new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: STALE_TIME,
                cacheTime: CACHE_TIME,
                retry: shouldRetryQuery,
                // The offline-first host owns its focus refresh schedule.
                // Web keeps the React Query default.
                refetchOnWindowFocus:
                    getPlatform().bootstrap.queryNetworkMode === "offlineFirst" ? false : undefined,
            },
        },
        mutationCache: new MutationCache({
            onError: (error: unknown, _variables, _context, mutation) => {
                if (
                    typeof mutation.options.onError === "function" ||
                    mutation.meta?.suppressGlobalError === true
                ) {
                    return;
                }
                useGlobalErrorSnackbarStore
                    .getState()
                    .showError(withRequestReference(GENERIC_MUTATION_ERROR_MESSAGE, error));
            },
        }),
    });
}

/** Marker substring of an Azure Blob SAS token's signature query parameter. */
const SAS_SIGNATURE_MARKER = "sig=";

/** Placeholder written in place of a SAS-bearing value. */
const REDACTED_PLACEHOLDER = "[redacted]";

/** One query's serialized snapshot entry, reused until its data changes. */
interface PersistedQueryFragment {
    /** Data update stamp and invalidation state the fragment was built from. */
    stamp: string;
    json: string | null;
    bytes: number;
    /** Time its data last changed; bounded eviction retains the newest content. */
    updatedAt: number;
    priority: number;
}

/** Per-session fragment cache, keyed by query hash. Exported for unit testing. */
export type PersistedQueryFragments = Map<string, PersistedQueryFragment>;

// Fragments avoid re-stringifying a large unchanged query when a small polling
// query updates. They are an optimization, not a second unbounded cache: the
// QueryCache owns their lifetime. Keep enough recently visited document data for
// a useful reload, but cap both the retained payload and number of entries.
const MAX_PERSISTED_FRAGMENT_BYTES = 8 * 1024 * 1024;
const MAX_PERSISTED_FRAGMENT_COUNT = 200;

export const BROWSER_PERSISTENCE_LIMITS = {
    maxBytes: 1024 * 1024,
    maxQueryBytes: 256 * 1024,
    maxQueries: 100,
} as const;
interface PersistenceLimits {
    maxBytes: number;
    maxQueryBytes: number;
    maxQueries: number;
}
const HOST_PERSISTENCE_LIMITS: PersistenceLimits = {
    maxBytes: MAX_PERSISTED_FRAGMENT_BYTES,
    maxQueryBytes: MAX_PERSISTED_FRAGMENT_BYTES,
    maxQueries: MAX_PERSISTED_FRAGMENT_COUNT,
};

/** UTF-8 byte length without allocating a second copy of a potentially multi-MB JSON string. */
function utf8ByteLength(value: string): number {
    let bytes = 0;
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code < 0x80) bytes += 1;
        else if (code < 0x800) bytes += 2;
        else if (code >= 0xd800 && code <= 0xdbff && index + 1 < value.length) {
            const next = value.charCodeAt(index + 1);
            if (next >= 0xdc00 && next <= 0xdfff) {
                bytes += 4;
                index += 1;
            } else bytes += 3;
        } else bytes += 3;
    }
    return bytes;
}

/**
 * One query as it goes to disk: the data, the update stamp, and a normalized rest of
 * the state -- a snapshot taken mid-refetch must not hydrate `isFetching: true` (the
 * observer would wait for a fetch that no longer exists). SAS-bearing values are
 * redacted by content, not by field name: `ApiFile.fileUrl`/`originalFileUrl` (and
 * any other field that ends up carrying an Azure Blob SAS URL) grant unauthenticated
 * blob read access for years, and a durable store on a shared jobsite PC must not
 * hold them for up to `MAX_AGE`. A redacted query is written `isInvalidated: true`
 * with its real `dataUpdatedAt`: react-query refetches an invalidated query on its
 * next mount (and `fetchQuery` goes to the network), yet an observer still reports the
 * hydrated rows as `success` while that refetch runs -- the ledger paints the sheet
 * set from the snapshot and the placeholders are replaced one round trip later. A
 * `dataUpdatedAt` of 0 would instead make the observer report `loading` and hide the
 * rows behind the network (react-query v3 `queryObserver.createResult`) -- which is
 * exactly what the restore side does to a stale EMPTY list, where there are no rows
 * to hide (`hintStaleEmptyAnswers` in `deserializePersistedClient`). `null` when the
 * data cannot be serialized.
 */
function persistedQueryJson(query: Query): string | null {
    let redacted = false;
    const data = JSON.stringify(query.state.data, (_key, value: unknown) => {
        if (typeof value === "string" && value.includes(SAS_SIGNATURE_MARKER)) {
            redacted = true;
            return REDACTED_PLACEHOLDER;
        }
        return value;
    });
    if (data === undefined) return null;
    const { dataUpdateCount, dataUpdatedAt } = query.state;
    return (
        `{"state":{"data":${data},"dataUpdateCount":${dataUpdateCount},"dataUpdatedAt":${dataUpdatedAt},` +
        `"error":null,"errorUpdateCount":0,"errorUpdatedAt":0,"fetchFailureCount":0,"fetchMeta":null,` +
        `"isFetching":false,"isInvalidated":${redacted || query.state.isInvalidated},"isPaused":false,"status":"success"},` +
        `"queryKey":${JSON.stringify(query.queryKey)},"queryHash":${JSON.stringify(query.queryHash)}}`
    );
}

/**
 * Builds the snapshot string for `queryClient`. Only a query whose data changed since
 * its last fragment is stringified again: a multi-MB list persisted next to a small
 * polled status no longer costs a multi-MB stringify on every throttled tick. A
 * fragment is retained only while its query is live. The QueryCache's cacheTime
 * therefore provides the useful reload window without turning serialized fragments
 * into a session-long, unbounded duplicate of document payloads. Exported for unit
 * testing.
 */
export function serializePersistedClient(
    queryClient: QueryClient,
    buster: string,
    shouldPersist: (query: Query) => boolean,
    fragments: PersistedQueryFragments = new Map(),
    now = Date.now(),
    limits: PersistenceLimits = HOST_PERSISTENCE_LIMITS,
): string {
    const prefix = `{"buster":${JSON.stringify(buster)},"timestamp":${now},"clientState":{"mutations":[],"queries":[`;
    const suffix = "]}}";
    const envelopeBytes = utf8ByteLength(prefix + suffix);
    const liveHashes = new Set<string>();
    for (const query of queryClient.getQueryCache().getAll()) {
        liveHashes.add(query.queryHash);
        if (!shouldPersist(query)) {
            fragments.delete(query.queryHash);
            continue;
        }
        const stamp = `${query.state.dataUpdatedAt}:${query.state.dataUpdateCount}:${query.state.isInvalidated}`;
        const existing = fragments.get(query.queryHash);
        if (existing?.stamp === stamp) continue;
        const json = persistedQueryJson(query);
        if (json !== null) {
            const bytes = utf8ByteLength(json);
            const fits =
                bytes <= Math.max(limits.maxQueryBytes, persistedByteAllowance(query.queryKey));
            fragments.set(query.queryHash, {
                stamp,
                // Remember a rejected stamp too: unrelated polling must not
                // stringify the same oversized answer on every write.
                json: fits ? json : null,
                bytes: fits ? bytes : 0,
                updatedAt: now,
                priority: isPersistedQueryKey(query.queryKey, "browser") ? 1 : 0,
            });
        }
    }
    for (const queryHash of fragments.keys()) {
        if (!liveHashes.has(queryHash)) fragments.delete(queryHash);
    }
    let fragmentBytes = Array.from(fragments.values(), (fragment) => fragment.bytes).reduce(
        (total, bytes) => total + bytes,
        0,
    );
    const maxBytes = limits.maxBytes + persistedAllowanceBytes();
    const oldestFirst = Array.from(fragments.entries()).sort(
        ([, left], [, right]) => left.priority - right.priority || left.updatedAt - right.updatedAt,
    );
    for (const [queryHash, fragment] of oldestFirst) {
        if (
            fragments.size <= limits.maxQueries &&
            fragmentBytes + envelopeBytes + Math.max(0, fragments.size - 1) <= maxBytes
        )
            break;
        fragments.delete(queryHash);
        fragmentBytes -= fragment.bytes;
    }
    const queries = Array.from(fragments.values(), (fragment) => fragment.json)
        .filter((json) => json !== null)
        .join(",");
    return prefix + queries + suffix;
}

/** Drops write-excluded queries from a restored snapshot, including shop,
 *  administration and consultant-request payloads. Stores are
 *  scoped per-org/API-base/member now, so these should not be reachable in
 *  the first place — this is defense in depth for legacy snapshots written
 *  before the corresponding dehydration exclusion existed, and a backstop
 *  against any future scoping regression. */
function dropRoleGatedQueries(client: PersistedClient): PersistedClient {
    const queries = client.clientState?.queries;
    if (Array.isArray(queries)) {
        client.clientState.queries = queries.filter((query) => {
            const key = query.queryKey as readonly unknown[] | string;
            return shouldPersistQuery({ queryKey: key, state: query.state });
        });
    }
    return client;
}

/** Deserializes a persisted cache snapshot, dropping role-gated entries (shop-scoped and admin-tenancy) outright and
 *  demoting every stale empty list to a hint (`hintStaleEmptyAnswers`, as of `now`). A
 *  snapshot without a query list is malformed and rejected. Exported for unit testing. */
export function deserializePersistedClient(
    cachedString: string,
    now = Date.now(),
): PersistedClient {
    const client: unknown = JSON.parse(cachedString);
    if (
        typeof client !== "object" ||
        client === null ||
        !Array.isArray((client as PersistedClient).clientState?.queries)
    ) {
        throw new Error("Persisted query cache snapshot is malformed");
    }
    const restored = dropRoleGatedQueries(client as PersistedClient);
    // A feature snapshot may paint immediately, but cannot authorize this load.
    // Keep its timestamp/data for answer-first rendering; fetch once on mount.
    for (const query of restored.clientState.queries) {
        const key = query.queryKey;
        if (
            Array.isArray(key) &&
            key[0] === PrimeQueryKeys.projectSettingsFeatures[0] &&
            key[1] === PrimeQueryKeys.projectSettingsFeatures[1]
        )
            query.state.isInvalidated = true;
    }
    hintStaleEmptyAnswers(restored.clientState.queries, now);
    return restored;
}

/**
 * The dehydration filter, exported for tests: it is a security boundary, and its
 * failure mode is silent (a key-shape change stops matching and the sensitive
 * payload lands on disk) — the colocated test locks it to the real key builders.
 *
 * Role-gated payloads must never reach the durable store: clash-alert, feature-flag,
 * project-health, connection-health, fleet engagement, the whole revision-dash prefix, the whole
 * provisioning prefix, staff-roles inventories, and the admin-tenancy tree
 * carry admin-only review, audit, raw KV, environment, cross-tenant fleet,
 * sync/import, and authorization data, while clash overlay previews derive
 * unpublished locations. Member-scoped stores prevent a normal user swap from
 * restoring this data; exclusion also protects legacy snapshots and future
 * scoping regressions.
 */
// Shop-review payloads are per-USER (packages are private to their uploader).
// Stores are member-scoped now, so a no-logout user swap gets a fresh store —
// keeping these keys out of persistence is legacy-snapshot hardening / defense
// in depth against a scoping regression, not the primary isolation boundary.
// Locked to the real key builders so a rename can't silently re-enable
// persistence.
const SHOP_QUERY_KEY_ROOTS: readonly unknown[] = [
    PrimeQueryKeys.shopDrawingRevisions[0],
    PrimeQueryKeys.shopSheetFlagsPrefix[0],
    PrimeQueryKeys.shopRevisionFlagsPrefix[0],
    PrimeQueryKeys.shopDrawingSuggestions("")[0],
    PrimeQueryKeys.packageSheets("")[0],
];
/** Second key segment of the shop-typed document/section-group collections
 *  (`documentsByType` / `sectionGroupsByDocType`). */
const SHOP_DOC_TYPE_SEGMENT = PrimeQueryKeys.documentsByType("shop_drawing")[1];

/** Shared shop-scope predicate for BOTH persistence directions: dehydration
 *  (shouldPersistQuery) and restore (deserializePersistedClient) — a snapshot
 *  written before this exclusion existed must not hydrate either. */
function isShopScopedQueryKey(queryKey: readonly unknown[] | string): boolean {
    return (
        SHOP_QUERY_KEY_ROOTS.includes(queryKeyRoot(queryKey)) ||
        (Array.isArray(queryKey) && queryKey[1] === SHOP_DOC_TYPE_SEGMENT)
    );
}
/** Shared admin-tenancy predicate for BOTH persistence directions: dehydration
 *  (shouldPersistQuery) and restore (deserializePersistedClient) — this tree
 *  carries fresh platform/company-admin-gated fleet data, so a revoked
 *  administrator must never read it back from a per-org, not per-user, store. */
function isAdminTenancyQueryKey(queryKey: readonly unknown[] | string): boolean {
    return queryKeyRoot(queryKey) === PrimeQueryKeys.adminTenancyRoot[0];
}

/** Shared consultant-request predicate for BOTH persistence directions (shouldPersistQuery and
 *  restore): the approver's feed carries other people's emails, reasons and decision notes and is
 *  readable only by an access administrator, and the Add a consultant lookups and contact profile
 *  are Directory data; a per-org, not per-user, store must never hand them to whoever opens it
 *  after that administrator is revoked. Segments match `consultantRequestKey` (api/submittalConsultants.ts,
 *  under `controlKey`) and the `submittal-consultant-*` roots of AddConsultantForm and the approver
 *  page; the persistence test builds the real key so a drift breaks there. */
function isConsultantRequestQueryKey(queryKey: readonly unknown[] | string): boolean {
    if (!Array.isArray(queryKey)) return false;
    const [root, second] = queryKey;
    return (
        (root === "document-workflows" && second === "submittal-consultant-requests") ||
        (typeof root === "string" && root.startsWith("submittal-consultant-"))
    );
}

export function shouldPersistQuery(query: {
    queryKey: readonly unknown[] | string;
    state: { status: string; data: unknown };
}): boolean {
    const isFeatureFlagInventory =
        Array.isArray(query.queryKey) &&
        query.queryKey.length === PrimeQueryKeys.projectSettingsFeatureFlags.length &&
        query.queryKey.every(
            (segment, index) => segment === PrimeQueryKeys.projectSettingsFeatureFlags[index],
        );
    // Every staff flag query (the global flags, the all-tenants choice settings) starts with
    // the global flags key.
    const isGlobalFlags =
        Array.isArray(query.queryKey) &&
        query.queryKey.length >= PrimeQueryKeys.globalFlags.length &&
        PrimeQueryKeys.globalFlags.every((segment, index) => query.queryKey[index] === segment);
    // Project-health payloads are admin-only, and the fleet key carries
    // cross-tenant data; a per-org (not per-user) store must never restore
    // them for a non-admin who takes over the browser without a logout.
    const isProjectHealth = query.queryKey[0] === PrimeQueryKeys.projectHealthStatus[0];
    // Same rationale for the connection-health station: every key hangs off one
    // root, so this line covers the overview, snapshots, groups, group sheets and
    // the cross-tenant fleet.
    const isConnectionHealth = query.queryKey[0] === PrimeQueryKeys.connectionHealthRoot[0];
    // Usage (`components/usage`) is staff-only and cross-tenant: every key hangs off one root.
    const isUsage = query.queryKey[0] === PrimeQueryKeys.usageAll[0];
    const isFleetEngagementReport =
        query.queryKey[0] === PrimeQueryKeys.fleetEngagementReportRoot[0] &&
        query.queryKey[1] === PrimeQueryKeys.fleetEngagementReportRoot[1];
    // Every revision-dash key (fleet, tenant, tenant-live, catch-up, backlog,
    // sync-status, project-bootstrap, project-processing-sets, auto-import,
    // digest, procore-projects, acc-projects, ...) is staff-only and carries
    // cross-tenant fleet status or integration project listings — the whole
    // prefix must stay out of the per-org (not per-user) store, not just the
    // fleet key.
    const isRevisionDash =
        query.queryKey[0] === PrimeQueryKeys.revisionDashRoot[0] &&
        query.queryKey[1] === PrimeQueryKeys.revisionDashRoot[1];
    // Run progress carries staff-only cross-tenant targets and local machine paths.
    const isMirandaRuns =
        query.queryKey[0] === PrimeQueryKeys.mirandaRunsRoot[0] &&
        query.queryKey[1] === PrimeQueryKeys.mirandaRunsRoot[1];
    // Recorded Marvin runs hold customer questions, answers and sheet crops.
    const isMarvinEvalRuns =
        query.queryKey[0] === PrimeQueryKeys.marvinEvalRunsRoot[0] &&
        query.queryKey[1] === PrimeQueryKeys.marvinEvalRunsRoot[1];
    // The precon ontology viewer (Admin › Ontology and the viewer's inspector) is staff-only: raw
    // object records, relations and crops a revoked staff member must not rehydrate.
    const isPreconOntology =
        query.queryKey[0] === PrimeQueryKeys.preconOntologyRoot[0] &&
        query.queryKey[1] === PrimeQueryKeys.preconOntologyRoot[1];
    // Marvin prompts and tenants' additional instructions are staff-only and customer-specific.
    const isMarvinSettings =
        query.queryKey[0] === PrimeQueryKeys.marvinSettingsRoot[0] &&
        query.queryKey[1] === PrimeQueryKeys.marvinSettingsRoot[1];
    // The Admin console reads every company, project and person across the platform.
    const isAdminConsole =
        query.queryKey[0] === PrimeQueryKeys.adminConsoleRoot[0] &&
        query.queryKey[1] === PrimeQueryKeys.adminConsoleRoot[1];
    // The staff-roles roster is the raw authorization decision
    // (usePrimePointStaffRoles derives isAdmin from it) — a revoked staff
    // member reloading under the same org must never rehydrate a stale
    // "primepoint_admin" entry from a per-org, not per-user, store.
    const isStaffRoles = query.queryKey[0] === "me" && query.queryKey[1] === "staff-roles";
    // A constructability group's member list is admin-only and carries the
    // unpublished issues the customer was deliberately not shown. The store is
    // per-org, not per-user, so persisting it would let a revoked reviewer
    // rehydrate those details from browser storage long after
    // /group_members starts returning 403 — the same reason isProjectHealth is
    // excluded.
    const isIssueGroupMembers =
        query.queryKey[0] === PrimeQueryKeys.issueGroupMembersAll[0] &&
        query.queryKey[1] === PrimeQueryKeys.issueGroupMembersAll[1];
    // Company/project administration tree -- see isAdminTenancyQueryKey.
    const isAdminTenancy = isAdminTenancyQueryKey(query.queryKey);
    const isShopScoped = isShopScopedQueryKey(query.queryKey);
    const isConsultantRequest = isConsultantRequestQueryKey(query.queryKey);
    if (
        queryKeyRoot(query.queryKey) === "project-access" ||
        query.queryKey[0] === "redesign-rollout" ||
        query.queryKey[0] === PrimeQueryKeys.clashAlerts[0] ||
        query.queryKey[0] === PrimeQueryKeys.clashAlertOverlayPreviews[0] ||
        // Admin precon runs carry held finding counts and internal notes; the
        // store is per-org, not per-user.
        query.queryKey[0] === PrimeQueryKeys.preconRuns[0] ||
        // Workbench and Explore reads are filtered for the caller, and the server
        // tells enforced projects' browsers to keep nothing (`no-store`).
        query.queryKey[0] === PrimeQueryKeys.workbenchRoot[0] ||
        isFeatureFlagInventory ||
        isGlobalFlags ||
        isProjectHealth ||
        isConnectionHealth ||
        isUsage ||
        isFleetEngagementReport ||
        isRevisionDash ||
        isMirandaRuns ||
        isMarvinEvalRuns ||
        isPreconOntology ||
        isMarvinSettings ||
        isAdminConsole ||
        isStaffRoles ||
        query.queryKey[0] === "drawing-review-snapshot" ||
        // Marvin artifact bodies: an image's Blob does not survive a snapshot.
        query.queryKey[0] === PrimeQueryKeys.marvinArtifact("", null, "html")[0] ||
        isIssueGroupMembers ||
        isAdminTenancy ||
        isConsultantRequest ||
        isShopScoped
    ) {
        return false;
    }
    return query.state.status === "success" && query.state.data !== undefined;
}

/**
 * The browser hosts' store: one IndexedDB database per tenant scope through
 * localforage. A dedicated instance per org, not the shared `localforage`
 * singleton: localforage.config() silently rejects reconfiguration after first use,
 * so a second call for a different org would otherwise resolve to a rejected
 * promise nobody awaits and every org keep persisting into the first org's
 * physical IndexedDB database.
 */
function createIndexedDbQueryCacheStore(
    cacheScope: string,
    orgSlug: string,
    apiBase: string,
): QueryCacheStore {
    const databaseName = `${CACHE_DATABASE_PREFIX}${cacheScope}`;
    const tenantStore = localforage.createInstance({
        name: databaseName,
        storeName: CACHE_STORE_NAME,
        description: `Primepoint API cache for ${orgSlug} at ${apiBase}`,
    });
    registerTenantStore(databaseName, tenantStore);
    return {
        read: async (scope) =>
            (await tenantStore.getItem<string>(`REACT_QUERY_OFFLINE_CACHE_${scope}`)) ?? null,
        write: async (scope, snapshot) => {
            await tenantStore.setItem(`REACT_QUERY_OFFLINE_CACHE_${scope}`, snapshot);
        },
        remove: (scope) => tenantStore.removeItem(`REACT_QUERY_OFFLINE_CACHE_${scope}`),
    };
}

/** Remembers a scope written to the host's own store for logout (a host store has no
 *  enumeration, so the scopes to remove come from the registry). */
function registerHostQueryCacheScope(cacheScope: string): void {
    const scopes = readStringRegistry(QUERY_CACHE_SCOPE_REGISTRY_KEY);
    scopes.add(cacheScope);
    writeStringRegistry(QUERY_CACHE_SCOPE_REGISTRY_KEY, scopes);
}

/**
 * Initialize persistence for a QueryClient scoped to one org, API base and member.
 *
 * Restore first, then persist: every query success schedules a throttled write of the
 * whole snapshot (built incrementally, see `serializePersistedClient`). A host that
 * publishes its own store (`Platform.queryCache`, the desktop) hydrates the snapshot
 * before `ready` resolves, and the provider renders the tenant subtree only then, so a
 * screen's first render already sees the persisted rows; the keys the screens register
 * through `persistQueryKeys` persist for that first render. Browser hosts (web,
 * iOS) keep IndexedDB and briefly wait for a bounded startup snapshot. Admission
 * gates every host; a late read only fills queries the network has not settled.
 * Scope: the `prime_enable_local_cache` opt-in cookie widens the default allowlist to
 * the whole cache on every host. `shouldPersistQuery`'s role-gated exclusions apply to
 * all of it.
 *
 * `isCurrent` guards the async restore: by the time the store answers the org may
 * have changed again — a stale hydrate must not repopulate a client nobody is
 * rendering with anymore.
 */
function initializePersistence(
    queryClient: QueryClient,
    organizationId: string,
    orgSlug: string,
    apiBase: string,
    memberId: string,
    isCurrent: () => boolean,
): PersistenceHandle {
    // A login that cannot read the epoch marker cannot tell a pre-logout
    // snapshot from a current one, so it does not persist at all.
    const cacheEpoch = readQueryCacheEpoch();
    if (cacheEpoch === null) {
        return INERT_PERSISTENCE;
    }

    if (cacheClearInProgress) {
        // A logout clear that outlives clearQueryCacheForLogout's five-second
        // barrier can still be running when the next login mounts this
        // provider. Bailing out permanently would leave that whole session
        // without persistence: nothing re-renders when the clear settles.
        // Retry once it does, unless the provider was torn down or the tenant
        // moved on in the meantime. The retry re-reads the epoch, so it adopts
        // the post-logout one rather than the invalidated snapshot's.
        const pendingClear = cacheClearPromise;
        if (!pendingClear) {
            return INERT_PERSISTENCE;
        }
        let cancelled = false;
        let retried: PersistenceHandle | undefined;
        const ready = pendingClear
            .catch(() => undefined)
            .then(() => {
                if (cancelled || !isCurrent()) {
                    return;
                }
                retried = initializePersistence(
                    queryClient,
                    organizationId,
                    orgSlug,
                    apiBase,
                    memberId,
                    isCurrent,
                );
                return retried.ready;
            });
        return {
            ready,
            stop: () => {
                cancelled = true;
                retried?.stop();
            },
        };
    }

    const cacheScope = tenantCacheScope(organizationId, apiBase, memberId);
    const hostStore = getPlatform().queryCache;
    let store: QueryCacheStore | null = null;
    try {
        if (hostStore) {
            registerHostQueryCacheScope(cacheScope);
            store = hostStore;
        } else if (typeof window !== "undefined" && "indexedDB" in window) {
            store = createIndexedDbQueryCacheStore(cacheScope, orgSlug, apiBase);
        }
    } catch (error) {
        console.warn("Failed to initialize query persistence:", error);
        return INERT_PERSISTENCE;
    }
    if (!store) {
        return INERT_PERSISTENCE;
    }

    const cookieValue = getCookie(ENABLE_LOCAL_CACHE_COOKIE);
    const isFullCacheEnabled = cookieValue?.toLowerCase() === "true";
    const restrictToDocumentArea = !isFullCacheEnabled;
    // Both gates must pass: the document-area allowlist, full-cache opt-in or a
    // registration for this host decides scope, and shouldPersistQuery excludes
    // role-gated payloads (e.g. clash alerts) regardless of scope.
    const shouldPersistToDisk = (query: Query) =>
        shouldPersistQuery(query) &&
        // Areas has a committed family snapshot. Old React Query snapshots must
        // neither be written nor revived after the cutover.
        (getPlatform().offline?.areas === undefined ||
            queryKeyRoot(query.queryKey) !== PrimeQueryKeys.areas[0]) &&
        (shouldDehydrateQuery(query, restrictToDocumentArea) ||
            (isPersistedQueryKey(query.queryKey, hostStore ? "host" : "browser") &&
                query.state.status === "success" &&
                query.state.data !== undefined));

    let isActive = true;
    let unsubscribe: (() => void) | undefined;
    const sessionGeneration = persistenceGeneration;
    const pendingStorageMutations = new Set<Promise<void>>();
    // A protected project's snapshot also names the authority it was written
    // under: after a role, grant or policy change the restore rejects and removes
    // it, and writes stop as soon as the authority moves (D8).
    const authority = protectedCacheAuthority();
    const cacheBuster = `${CACHE_BUSTER}-${cacheScope}${cacheEpoch ? `-epoch:${cacheEpoch}` : ""}${
        authority ? `-authority:${authority}` : ""
    }`;
    const fragments: PersistedQueryFragments = new Map();
    let hydrating = false;
    let lastWriteAt = 0;
    let writeTimer: ReturnType<typeof setTimeout> | undefined;
    let writeDueAt = 0;
    let writeRequested = false;
    let currentWrite: Promise<void> | null = null;

    const canPersist = () =>
        isActive &&
        !cacheClearInProgress &&
        sessionGeneration === persistenceGeneration &&
        readQueryCacheEpoch() === cacheEpoch &&
        protectedCacheAuthority() === authority &&
        isCurrent();

    const runTrackedStorageMutation = async (operation: () => Promise<unknown>): Promise<void> => {
        if (!canPersist()) {
            return;
        }

        // Register before yielding, then re-check the generation in the
        // microtask that actually starts the mutation. Logout can therefore
        // invalidate a write even if a throttled callback has just fired.
        const mutation = Promise.resolve().then(async () => {
            if (!canPersist()) {
                return;
            }
            await operation();
        });
        pendingStorageMutations.add(mutation);
        try {
            await mutation;
        } finally {
            pendingStorageMutations.delete(mutation);
        }
    };

    const drainStorageMutations = async () => {
        while (pendingStorageMutations.size > 0) {
            await Promise.allSettled(Array.from(pendingStorageMutations));
        }
    };

    const persistNow = (): Promise<void> => {
        writeRequested = true;
        if (currentWrite) return currentWrite;
        // One store write at a time. Changes during a slow write coalesce into
        // one subsequent snapshot of the latest cache, never an unbounded queue
        // or an older write finishing after a newer one. Flush awaits both.
        currentWrite = runTrackedStorageMutation(async () => {
            while (writeRequested && canPersist()) {
                writeRequested = false;
                lastWriteAt = Date.now();
                await store!.write(
                    cacheScope,
                    serializePersistedClient(
                        queryClient,
                        cacheBuster,
                        shouldPersistToDisk,
                        fragments,
                        lastWriteAt,
                        hostStore ? HOST_PERSISTENCE_LIMITS : BROWSER_PERSISTENCE_LIMITS,
                    ),
                );
            }
        })
            .catch((error) => {
                console.warn("Failed to persist query cache:", error);
            })
            .finally(() => {
                currentWrite = null;
                // A flush can request the next snapshot after the loop exits
                // but before this promise settles. Chain that write too, so
                // the caller still waits for its latest data to reach storage.
                if (writeRequested && canPersist()) return persistNow();
            });
        return currentWrite;
    };

    // Background updates use the wider throttle. Startup updates may pull that
    // timer forward; later events never postpone an already scheduled write.
    const schedulePersist = (startup = false) => {
        if (!canPersist()) return;
        // A new first viewport must not sit behind unrelated background polling's
        // ten-second throttle. Coalesce startup changes for one second instead.
        const dueAt = startup
            ? Date.now() + WRITE_DELAY
            : Math.max(Date.now() + WRITE_DELAY, lastWriteAt + THROTTLE_TIME);
        if (writeTimer !== undefined && writeDueAt <= dueAt) return;
        if (writeTimer !== undefined) clearTimeout(writeTimer);
        writeDueAt = dueAt;
        writeTimer = setTimeout(() => {
            writeTimer = undefined;
            void persistNow();
        }, dueAt - Date.now());
    };

    // A pending timer is the dirty flag: a write waiting on the throttle goes now.
    const flush = (): Promise<void> => {
        if (writeTimer === undefined) return currentWrite ?? Promise.resolve();
        clearTimeout(writeTimer);
        writeTimer = undefined;
        return persistNow();
    };

    // The page is going away or to the background (a quit, a reload, the desktop
    // window hidden): a write still waiting on the throttle would be lost with it,
    // so it goes now, best effort. The desktop's close handshake additionally awaits
    // `flushPersistedQueryCaches` before the window is destroyed.
    const flushOnPageHide = () => {
        void flush();
    };
    const flushOnHidden = () => {
        if (document.visibilityState === "hidden") void flush();
    };
    window.addEventListener("pagehide", flushOnPageHide);
    document.addEventListener("visibilitychange", flushOnHidden);

    let stopPromise: Promise<void> | null = null;
    const session: PersistenceSession = {
        flush,
        stop: () => {
            if (!stopPromise) {
                // These mutations are synchronous and happen before the first
                // await: subscribers and queued callbacks are neutralized before
                // logout starts clearing any store.
                isActive = false;
                unsubscribe?.();
                window.removeEventListener("pagehide", flushOnPageHide);
                document.removeEventListener("visibilitychange", flushOnHidden);
                if (writeTimer !== undefined) {
                    clearTimeout(writeTimer);
                    writeTimer = undefined;
                }
                stopPromise = drainStorageMutations().finally(() => {
                    activePersistenceSessions.delete(session);
                });
            }
            return stopPromise;
        },
    };
    activePersistenceSessions.add(session);

    const restore = async (): Promise<RestoreOutcome> => {
        try {
            const snapshot = await store!.read(cacheScope);
            // The effect cleanup may have retired this client while its store
            // read was pending. In that case it must not hydrate tenant data back
            // into memory.
            if (!canPersist() || snapshot === null) {
                return "miss";
            }
            if (
                utf8ByteLength(snapshot) >
                (hostStore ? HOST_PERSISTENCE_LIMITS : BROWSER_PERSISTENCE_LIMITS).maxBytes +
                    persistedAllowanceBytes()
            )
                throw new Error("Persisted query cache exceeds its storage budget");
            const persistedClient = deserializePersistedClient(snapshot);
            const expired =
                typeof persistedClient.timestamp !== "number" ||
                Date.now() - persistedClient.timestamp > MAX_AGE;
            if (expired || persistedClient.buster !== cacheBuster) {
                await runTrackedStorageMutation(() => store!.remove(cacheScope));
                return "rejected";
            }
            // A query the live cache already settled (a fetch that landed while the
            // read was pending: the browser hosts, and a host store past its deadline)
            // keeps its answer, success or error; the snapshot only fills the rest.
            // `hydrate` skips older data on its own, but a persisted stamp from a clock
            // since set back would otherwise beat a live one.
            const cache = queryClient.getQueryCache();
            const queries = persistedClient.clientState.queries.filter((query) => {
                if (
                    getPlatform().offline?.areas !== undefined &&
                    queryKeyRoot(query.queryKey as QueryKey) === PrimeQueryKeys.areas[0]
                )
                    return false;
                const status = cache.get(query.queryHash)?.state.status;
                if (status === "success" || status === "error") return false;
                return !persistedDependencies(query.queryKey as QueryKey).some((source) => {
                    return cache
                        .findAll(source)
                        .some(
                            (live) =>
                                live.state.status === "success" || live.state.status === "error",
                        );
                });
            });
            hydrating = true;
            try {
                hydrate(queryClient, { ...persistedClient.clientState, queries });
                // React Query invalidation emits events only for existing keys.
                // Idle dependency entries ensure an upload/delete can retire a
                // restored viewport even before the full collection mounts.
                for (const query of queries)
                    for (const source of persistedDependencies(query.queryKey as QueryKey))
                        cache.build(queryClient, { queryKey: source });
            } finally {
                hydrating = false;
            }
            restoredQueries = queries.length;
            restoredBytes = snapshot.length;
            return "hit";
        } catch (error) {
            console.warn("Failed to restore query cache:", error);
            try {
                await runTrackedStorageMutation(() => store!.remove(cacheScope));
            } catch (removeError) {
                console.warn("Failed to remove invalid query cache:", removeError);
            }
            return "rejected";
        }
    };

    // Hydration's own cache events carry no new data and are ignored; a query
    // success (a fetch, `setQueryData`) schedules the throttled write.
    unsubscribe = queryClient.getQueryCache().subscribe((event) => {
        if (hydrating || !event) return;
        if (
            event.type === "queryRemoved" ||
            (event.type === "queryUpdated" &&
                (event.action.type === "invalidate" || event.action.type === "success"))
        ) {
            for (const dependent of persistedDependents(event.query.queryKey))
                queryClient.removeQueries(dependent);
        }
        if (event.type === "queryRemoved") {
            fragments.delete(event.query.queryHash);
            schedulePersist(isPersistedQueryKey(event.query.queryKey, "browser"));
            return;
        }
        if (
            event.type === "queryUpdated" &&
            (event.action.type === "success" || event.action.type === "invalidate")
        )
            schedulePersist(isPersistedQueryKey(event.query.queryKey, "browser"));
    });

    // Under a host store the measure names how the first tenant render got its rows:
    // `hit` (the snapshot hydrated before the render), `deadline` (the render went
    // ahead; the read landed later or not at all), `miss`, or `rejected` (expired or
    // another schema). Every host reports its bounded wait after admission.
    let restoredQueries = 0;
    let restoredBytes = 0;
    const restoreStartedAt = performance.now();
    const restored = restore();
    const stop = () => {
        void session.stop();
    };
    const deadline = new Promise<RestoreOutcome>((resolve) => {
        const timer = setTimeout(
            () => resolve("deadline"),
            hostStore ? RESTORE_DEADLINE : BROWSER_RESTORE_DEADLINE,
        );
        void restored.then(() => clearTimeout(timer));
    });
    return {
        ready: Promise.race([restored, deadline]).then((outcome) => {
            e2ePublish.measure("shell:query-cache:restore", restoreStartedAt, {
                outcome,
                queries: restoredQueries,
                bytes: restoredBytes,
            });
        }),
        stop,
    };
}

interface TenantQueryClientProviderProps {
    children: React.ReactNode;
    /** Authentication recovery only: never a protected app subtree. */
    admissionRecovery?: React.ReactNode;
    /** Public chrome while the admitted tenant subtree is not mounted. */
    fallback?: React.ReactNode;
}

/** The client the tenant subtree renders under once admission and host snapshot
 *  readiness permit it. */
interface ActiveTenantClient {
    organizationId: string | null;
    queryClient: QueryClient;
}

/** A tenant memory no host publishes: nothing remembered, nothing to observe. */
const NO_LAST_SESSION: LastSessionMemory = { read: () => null, subscribe: () => () => undefined };

/**
 * Provides a tenant-scoped QueryClient that isolates cache per organization, backend
 * and member.
 *
 * When the organization, member, or resolved API base changes, the previous tenant
 * subtree unmounts in that same render and its client is torn down (persistence
 * stopped, registrations withdrawn, cache cleared) before the successor's persisted
 * snapshot is even read; the subtree renders again after admission and snapshot
 * readiness (bounded on every host), which:
 * - Automatically clears all in-flight queries
 * - Clears file cache (OPFS) from the previous org
 * - Provides a fresh cache for the new org, already holding its persisted rows
 * - Initializes persistence scoped to the new org, backend base URL and member
 *
 * This eliminates the need for query key prefixing - each tenant naturally
 * gets their own isolated cache by having their own QueryClient instance.
 */
export function TenantQueryClientProvider({
    children,
    admissionRecovery,
    fallback = null,
}: TenantQueryClientProviderProps) {
    const { organization } = useCompatOrganization();
    const { member } = useCompatMember();
    const { tokens } = useEmbedAuth();
    const lastSession = usePlatform().tenant.lastSession ?? NO_LAST_SESSION;
    const sessionJwt = tokens?.sessionJwt;
    const jwtIdentity = useMemo(() => sessionIdentityFromJwt(sessionJwt), [sessionJwt]);
    // The host's remembered session names the scope until a verified one lands, so the
    // first tenant render hydrates that scope's snapshot while the host's session
    // exchange is still on the network. The host publishes it only once it has proven
    // locally that the session it holds is the one that verified as that identity
    // (`LastSessionMemory`). The same identity then arrives in the JWT and nothing
    // rotates; a different one (or the host settling signed out) rotates the client,
    // and the provisional one is torn down before it could write a snapshot.
    const remembered: SessionIdentity | null = useSyncExternalStore(
        lastSession.subscribe,
        lastSession.read,
    );
    const sessionIdentity = jwtIdentity ?? remembered;
    // A canonical company/project session partitions the cache by its validated
    // scope key, not by the Stytch organization: one organization can serve several
    // projects, and their caches must not share a store. Web URL admission lives
    // above this cache's remount boundary; desktop still owns one cache per window
    // above its per-tab routers. Both observe the same admitted scope store. (PP-827)
    const tenantContentScopeKey = useSyncExternalStore(
        subscribeTenantContentScopeKey,
        currentTenantContentScopeKey,
    );
    // A staff registry session holds no project, and TenantScopeProvider lets it reach only a
    // project-free page (the Admin console): its client names no organization, so nothing
    // asks the server to admit a project, no access scope is set and nothing persists.
    const projectFree = useTenantScope().staffRegistrySession;
    // `/api/ai/session/me` remains the authority once it answers; the session JWT
    // names the same tenant one round trip earlier.
    const organizationId = projectFree
        ? null
        : (tenantContentScopeKey ??
          organization?.organization_id ??
          sessionIdentity?.organizationId ??
          null);
    const orgSlug = projectFree
        ? null
        : (organization?.organization_slug ?? sessionIdentity?.organizationSlug ?? null);
    const memberId = member?.member_id ?? sessionIdentity?.memberId ?? null;
    const apiBase = resolveCacheApiBase();
    const identityScope = tenantCacheScope(
        organizationId ?? "__signed_out__",
        apiBase ?? "__no_http_api_base__",
        memberId ?? "__no_member__",
    );

    const nextAccessScope = organizationId && memberId ? identityScope : null;
    const [committedAccessScope, setCommittedAccessScope] = useState<string | null | undefined>();
    const accessEpoch = useSyncExternalStore(subscribeResourceAccess, resourceAuthorityEpoch);
    // A speculative render may select a different project and then be abandoned.
    // Only a committed scope may revoke authority. Until this effect commits it,
    // the new subtree and its durable cache remain closed.
    useEffect(() => {
        setResourceAccessScope(nextAccessScope);
        setCommittedAccessScope(nextAccessScope);
    }, [nextAccessScope]);
    const scopeReady = committedAccessScope === nextAccessScope;
    const queryClientScope = `${identityScope}|access:${accessEpoch}`;

    // Track previous org for debug logging
    const prevOrgSlugRef = useRef<string | null>(orgSlug);
    // Tracks whichever client the effect below most recently prepared, so a late
    // persistence restore can tell it's targeting a superseded client.
    const currentQueryClientRef = useRef<QueryClient | null>(null);
    const [active, setActive] = useState<ActiveTenantClient | null>(null);
    const admission = useResourceAdmission(
        identityScope,
        scopeReady && Boolean(organizationId && memberId),
    );
    const admissionReady = scopeReady && admission.ready;

    // Create a new QueryClient when either tenant or backend changes. Use the
    // stable organization ID rather than the mutable/reusable slug; native
    // local/staging/prod environments also share one WebView origin.
    // Construction only. Scope mutation, cache preparation and publication happen
    // after commit, before this provider permits any descendant to mount.
    const queryClient = useMemo(() => {
        try {
            return createQueryClient();
        } catch (error) {
            console.error("Failed to create QueryClient:", error);
            return new QueryClient();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryClientScope]);

    const retained = active?.queryClient === queryClient ? active : null;
    // Only a clock expiry may retain the mounted editor/viewer. Its content is
    // hidden and inaccessible until the same authority is verified again.
    // Identity, policy, capability and definitive-denial changes create a new
    // client and dispose the previous subtree as before.
    const paused = scopeReady && admission.suspended && retained !== null;
    const keepPrepared = admissionReady || paused;

    useEffect(() => {
        currentQueryClientRef.current = queryClient;
        previewManager.clear();
        previewManager.setActiveOrganization(tenantContentScopeKey ?? orgSlug);

        // Retire/register the old org-only database even when current
        // persistence is disabled or the configured API base is invalid.
        // Otherwise a pre-registry snapshot can remain undiscoverable on Safari.
        if (
            orgSlug &&
            typeof window !== "undefined" &&
            "indexedDB" in window &&
            !cacheClearInProgress
        ) {
            retireLegacyTenantCache(orgSlug);
            if (organizationId && apiBase) {
                // The 0.4/0.5-era org--apiBase store predates member scoping
                // and may hold another member's private shop payloads.
                retirePreMemberTenantCache(organizationId, apiBase);
            }
        }

        // Durable persistence additionally waits for the member identity: the
        // provisional pre-auth client must never write a store another member
        // could hydrate.
        if (!keepPrepared) return;
        let admittedPersistence = INERT_PERSISTENCE;
        let cancelled = false;
        const ready = (async () => {
            if (organizationId && apiBase && memberId) {
                admittedPersistence = initializePersistence(
                    queryClient,
                    organizationId,
                    orgSlug ?? organizationId,
                    apiBase,
                    memberId,
                    () => currentQueryClientRef.current === queryClient && offlineAccessAllowed(),
                );
            }
            await admittedPersistence.ready;
        })();
        const persistence: PersistenceHandle = {
            ready,
            stop: () => {
                cancelled = true;
                admittedPersistence.stop();
            },
        };

        let unsubscribeAreas: (() => void) | undefined;
        void persistence.ready
            .then(() => {
                if (cancelled || currentQueryClientRef.current !== queryClient) return;
                // Published before the tenant subtree renders under this client: a screen's
                // mount effect runs before this provider's own effects, and a read outside
                // React from that effect (the working set's document list) must dedupe
                // through this client and its hydrated snapshot, not go to the network.
                if (getPlatform().offline?.answer !== undefined)
                    registerAnswerFirstQueryClient(queryClient);
                unsubscribeAreas = getPlatform().offline?.areas?.subscribeCommitted(() => {
                    if (currentQueryClientRef.current === queryClient) {
                        void queryClient.invalidateQueries(PrimeQueryKeys.areas);
                    }
                });
                // Only a tenant client (one with a member) is offered to reads outside React.
                setActiveTenantQueryClient(organizationId ? queryClient : null);
                setActive({ organizationId, queryClient });
            })
            .catch((error) => {
                if (currentQueryClientRef.current !== queryClient || cancelled) return;
                console.error("Project cache initialization failed", error);
            });

        return () => {
            // Stop persistence first: clear() emits QueryCache removal events, which
            // must not replace the org's durable snapshot with an empty one during an
            // org switch or unmount. Tenant-owned non-query work (registered via
            // queryClientLifecycle) stops before the cache it depends on is cleared.
            // The registrations are withdrawn here too: a successor publishes its own
            // once it is ready, and until then reads outside React have no client.
            persistence.stop();
            unsubscribeAreas?.();
            disposeQueryClientResources(queryClient);
            queryClient.clear();
            if (getPlatform().offline?.answer !== undefined) registerAnswerFirstQueryClient(null);
            setActiveTenantQueryClient(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryClient, keepPrepared]);

    useEffect(() => {
        const available = admissionReady && retained ? queryClient : null;
        setActiveTenantQueryClient(available);
        if (getPlatform().offline?.answer !== undefined) registerAnswerFirstQueryClient(available);
        if (paused) void queryClient.cancelQueries();
    }, [queryClient, admissionReady, retained, paused]);

    // Track org changes for debugging
    // Note: File cache is tenant-scoped (/files/{orgSlug}/) so no clearing needed on org switch.
    // Cache is only cleared on logout via performLogout().
    useEffect(() => {
        const prevOrg = prevOrgSlugRef.current;
        if (prevOrg !== orgSlug) {
            PrimeDebugLog("TenantQueryClient", `orgSlug changed: ${prevOrg} → ${orgSlug}`);
        }
        prevOrgSlugRef.current = orgSlug;
    }, [orgSlug]);

    // React Query v3 creates each QueryObserver against the client that was
    // current when the consumer mounted; changing only the context value does
    // not rebind an existing observer. The tenant subtree renders only under the
    // current client, so a scope change unmounts it in the same render (org B never
    // reads or fetches through org A's retired client, and nothing of A stays on
    // screen while B restores) and mounts it again once B's client is active. On a
    // host store the first render of every screen already has its persisted rows.
    const shown = admissionReady ? retained : null;
    const mounted = shown ?? (paused ? retained : null);
    // Revocation/epoch changes fence children before the next admission settles.
    // Keep guidance visible during that gap as well as after a failed retry.
    const accessUnavailable =
        admission.error || (!admissionReady && resourceAccessState() !== null);
    return (
        <TenantOrganizationIdContext.Provider value={mounted?.organizationId ?? undefined}>
            {mounted ? (
                <QueryClientProvider client={mounted.queryClient}>
                    <div
                        style={{ display: paused ? "none" : "contents" }}
                        aria-hidden={paused || undefined}
                    >
                        {children}
                    </div>
                    {paused && (
                        <AccessSuspensionCurtain>
                            <EmptyState
                                title={
                                    admission.error
                                        ? "Project access unavailable"
                                        : "Checking project access"
                                }
                                hint={
                                    admission.error
                                        ? "Reconnect and retry. Your changes are kept in this tab while access is checked."
                                        : "Your work will return when access is verified."
                                }
                                action={
                                    <>
                                        <Quiet onClick={admission.retry}>Retry</Quiet>
                                        {admissionRecovery}
                                    </>
                                }
                            />
                        </AccessSuspensionCurtain>
                    )}
                </QueryClientProvider>
            ) : accessUnavailable ? (
                <div role="alert">
                    <EmptyState
                        title="Project access unavailable"
                        hint={`${admission.error ?? "Your project access has changed"}. Retry to check access again, or contact your project administrator.`}
                        action={
                            <>
                                <Quiet onClick={admission.retry}>Retry</Quiet>
                                {admissionRecovery}
                            </>
                        }
                    />
                </div>
            ) : (
                fallback
            )}
        </TenantOrganizationIdContext.Provider>
    );
}

/**
 * Clear all persisted caches across all orgs.
 * Use this on logout to ensure complete cleanup. The returned promise resolves
 * only after active writes have drained and every discoverable per-org store
 * has been cleared.
 */
async function clearTenantDatabase(databaseName: string): Promise<boolean> {
    let tenantStores = Array.from(tenantStoresByDatabase.get(databaseName) ?? []);

    // A database remembered in localStorage may come from an earlier page
    // load, so there may be no live instance in this JavaScript runtime.
    if (tenantStores.length === 0) {
        try {
            tenantStores = [
                localforage.createInstance({
                    name: databaseName,
                    storeName: CACHE_STORE_NAME,
                }),
            ];
        } catch (error) {
            console.warn(`Failed to open persisted cache ${databaseName}:`, error);
            return false;
        }
    }

    const results = await Promise.allSettled(
        tenantStores.map((tenantStore) => Promise.resolve().then(() => tenantStore.clear())),
    );
    const succeeded = results.some((result) => result.status === "fulfilled");

    for (const result of results) {
        if (result.status === "rejected") {
            console.warn(`Failed to clear persisted cache ${databaseName}:`, result.reason);
        }
    }

    return succeeded;
}

/** Removes every scope this page wrote to the host's own store; the scopes that
 *  failed stay registered for the next logout. */
async function clearHostQueryCacheScopes(): Promise<void> {
    const scopes = readStringRegistry(QUERY_CACHE_SCOPE_REGISTRY_KEY);
    if (scopes.size === 0) return;
    let resolvedStore: QueryCacheStore | undefined;
    try {
        resolvedStore = getPlatform().queryCache;
    } catch {
        resolvedStore = undefined;
    }
    if (!resolvedStore) return;
    const hostStore = resolvedStore;
    const failed = new Set<string>();
    await Promise.all(
        Array.from(scopes, async (scope) => {
            try {
                await hostStore.remove(scope);
            } catch (error) {
                failed.add(scope);
                console.warn("Failed to clear the persisted query cache for a tenant:", error);
            }
        }),
    );
    writeStringRegistry(QUERY_CACHE_SCOPE_REGISTRY_KEY, failed);
}

async function performPersistedCacheClear(): Promise<void> {
    // Invalidate every storage adapter before the first await. Any already
    // scheduled throttled callback will observe the new generation and
    // become a no-op instead of recreating data after its store is cleared.
    cacheClearInProgress = true;
    persistenceGeneration += 1;

    try {
        const sessions = Array.from(activePersistenceSessions);
        await Promise.allSettled(sessions.map((session) => session.stop()));
        await Promise.allSettled(Array.from(legacyCacheRetirements.values()));
        legacyCacheRetirements.clear();

        await clearHostQueryCacheScopes();

        const databaseNames = readPersistedTenantDatabaseNames();
        tenantStoresByDatabase.forEach((_tenantStores, databaseName) => {
            databaseNames.add(databaseName);
        });

        if (typeof indexedDB !== "undefined" && typeof indexedDB.databases === "function") {
            try {
                const databases = await indexedDB.databases();
                for (const database of databases) {
                    if (database.name?.startsWith(CACHE_DATABASE_PREFIX)) {
                        databaseNames.add(database.name);
                    }
                }
            } catch (error) {
                // The registered createInstance stores below remain a reliable
                // fallback in Safari and privacy modes where enumeration is
                // missing or rejected.
                console.warn("Failed to enumerate IndexedDB databases:", error);
            }
        }

        const clearResults = await Promise.all(
            Array.from(databaseNames).map(async (databaseName) => ({
                databaseName,
                cleared: await clearTenantDatabase(databaseName),
            })),
        );

        const failedDatabaseNames = new Set<string>();
        for (const { databaseName, cleared } of clearResults) {
            if (cleared) {
                tenantStoresByDatabase.delete(databaseName);
            } else {
                failedDatabaseNames.add(databaseName);
            }
        }
        writeStringRegistry(CACHE_DATABASE_REGISTRY_KEY, failedDatabaseNames);
    } finally {
        cacheClearInProgress = false;
    }
}

export function clearAllPersistedCaches(): Promise<void> {
    if (!cacheClearPromise) {
        cacheClearPromise = performPersistedCacheClear().finally(() => {
            cacheClearPromise = null;
        });
    }
    return cacheClearPromise;
}

/**
 * Writes every tenant snapshot still waiting on the write throttle and resolves once
 * the stores have taken them. The desktop's close handshake (`request-close-document`
 * -> `confirm_close_window`) awaits this before the window is destroyed, so the last
 * seconds of a session -- the conversation just opened, the sheet just listed -- are
 * on disk for the next launch. A session with nothing pending resolves at once.
 */
export function flushPersistedQueryCaches(): Promise<void> {
    return Promise.allSettled(
        Array.from(activePersistenceSessions, (session) => session.flush()),
    ).then(() => undefined);
}
